import { ReactNode } from 'react';

import styled, { CSSObject } from 'styled-components';

import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

// Shared by both the VariableChip span and message-body chips rendered from raw
// <code> HTML (which can only be styled via this CSSObject). Font is inherited
// to match surrounding text; the themed-email call site adds `!important` on top
// to beat the wrapper's color rules.
export const variableChipStyles: CSSObject = {
  display: 'inline-block',
  backgroundColor: theme.color.blueTrans10,
  color: theme.color.elementBlue100,
  borderRadius: pxToRem(4),
  padding: `0 ${pxToRem(4)}`,
  fontFamily: 'inherit',
  fontWeight: 'inherit',
};

// Red override for a broken variable (deleted/severed source). Shared by the
// VariableChip span and the <ins>-wrapped <code> chips in the email preview and
// traveller sidepanel. `color` is `!important` so it wins in the themed-email
// wrapper, where the base `& code` rule forces blue with `!important` too.
export const brokenChipStyles: CSSObject = {
  backgroundColor: theme.color.redTrans10,
  color: `${theme.color.elementRed100} !important`,
};

const VariableChip = styled.span<{ $broken?: boolean }>(({ $broken }) => ({
  ...variableChipStyles,
  ...($broken && brokenChipStyles),
}));

const VARIABLE_BODY = /\{\{\s*[^{}]+?\s*\}\}/;
const VARIABLE_SPLIT_PATTERN = new RegExp(`(${VARIABLE_BODY.source})`, 'g');
const VARIABLE_MATCH_PATTERN = new RegExp(`^${VARIABLE_BODY.source}$`);

const chipLabel = (token: string): string => token.replace(/^\{\{\s*/, '').replace(/\s*\}\}$/, '');

export const renderWithVariableChips = (
  text: string,
  brokenLabels?: ReadonlySet<string>
): ReactNode =>
  text.split(VARIABLE_SPLIT_PATTERN).map((part, index) =>
    VARIABLE_MATCH_PATTERN.test(part) ? (
      <VariableChip key={`${index}-${part}`} $broken={brokenLabels?.has(chipLabel(part))}>
        {part}
      </VariableChip>
    ) : (
      part
    )
  );
